import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Wrench, XCircle, Radio } from "lucide-react";
import api from "../api";
import { Reveal } from "../components/motion";

const STATUS_META = {
  operational: { label: "Operational", color: "text-emerald-400", dot: "bg-emerald-400", icon: CheckCircle2 },
  degraded: { label: "Degraded", color: "text-amber-400", dot: "bg-amber-400", icon: AlertTriangle },
  outage: { label: "Outage", color: "text-red-400", dot: "bg-red-400", icon: XCircle },
  maintenance: { label: "Maintenance", color: "text-volt", dot: "bg-volt", icon: Wrench },
};

function barColor(pct) {
  if (pct >= 99.9) return "bg-emerald-400";
  if (pct >= 98) return "bg-emerald-500/70";
  return "bg-amber-400";
}

export default function StatusPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/status").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <main className="pt-32 min-h-screen text-center text-white/40 font-mono text-sm" data-testid="status-loading">Checking systems…</main>;
  }

  const meta = STATUS_META[data.overall] || STATUS_META.operational;
  const upPct = data.uptime.length
    ? (data.uptime.reduce((a, d) => a + d.pct, 0) / data.uptime.length).toFixed(2)
    : "100.00";

  return (
    <main className="pt-16 min-h-screen" data-testid="status-page">
      <section className="hero-glow border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> Live systems
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="status-headline">
              STATUS
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <div className={`mt-10 rounded-xl border p-6 flex items-center gap-4 ${
              data.overall === "operational" ? "border-emerald-400/30 bg-emerald-400/5" : "border-amber-400/30 bg-amber-400/5"
            }`} data-testid="status-overall">
              <span className="relative flex h-3.5 w-3.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${meta.dot} opacity-60`} />
                <span className={`relative inline-flex rounded-full h-3.5 w-3.5 ${meta.dot}`} />
              </span>
              <div>
                <p className={`font-heading font-bold text-lg ${meta.color}`}>
                  {data.overall === "operational" ? "All Systems Operational" : meta.label}
                </p>
                <p className="text-xs text-white/40 font-mono mt-0.5">90-day uptime: {upPct}% · bot {data.bot?.status || "online"} · {data.bot?.version}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
        <Reveal>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5" data-testid="status-components">
            {data.components.map((c) => {
              const m = STATUS_META[c.status] || STATUS_META.operational;
              return (
                <div key={c.id} className="flex items-center justify-between px-6 py-5" data-testid={`status-component-${c.key}`}>
                  <div className="flex items-center gap-3">
                    <Radio size={16} className="text-white/30" />
                    <span className="font-heading font-semibold text-white">{c.name}</span>
                  </div>
                  <span className={`flex items-center gap-2 text-sm font-medium ${m.color}`}>
                    <span className="relative flex h-2 w-2">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${m.dot} opacity-60`} />
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${m.dot}`} />
                    </span>
                    {m.label}
                  </span>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6" data-testid="uptime-history">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-white">Uptime — last 90 days</h3>
              <span className="font-mono text-sm text-emerald-400">{upPct}%</span>
            </div>
            <div className="flex gap-[2px] h-10 items-stretch">
              {data.uptime.map((d) => (
                <div key={d.id} title={`${d.date} — ${d.pct}%`} className={`flex-1 rounded-sm ${barColor(d.pct)} opacity-80 hover:opacity-100 transition-opacity duration-150`} />
              ))}
            </div>
            <div className="flex justify-between mt-3 text-[10px] font-mono text-white/30">
              <span>90 days ago</span>
              <span>Today</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <h3 className="font-heading font-bold text-white mb-5">Incident history</h3>
          {data.incidents.length === 0 ? (
            <p className="text-sm text-white/40" data-testid="no-incidents">No incidents on record. Silence is golden.</p>
          ) : (
            <div className="space-y-4" data-testid="incidents-list">
              {data.incidents.map((inc) => (
                <div key={inc.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p className="font-heading font-semibold text-white">{inc.title}</p>
                    <span className={`text-[10px] font-mono uppercase tracking-[0.2em] rounded-full px-3 py-1 border ${
                      inc.status === "resolved" ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"
                    }`}>{inc.status}</span>
                  </div>
                  <div className="mt-4 space-y-2 border-l-2 border-white/10 pl-4">
                    {inc.updates.map((u, i) => (
                      <p key={i} className="text-sm text-muted-foreground">
                        <span className="font-mono text-xs text-white/30 mr-3">{new Date(u.ts).toLocaleString()}</span>
                        {u.text}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Reveal>
      </section>
    </main>
  );
}
